import { computed, ComputedRef, Ref, unref } from 'vue';
import { t } from '@/ui/i18n/i18n';

export const parseConfidenceValue = (val: string): number | null => {
  if (!val) {
    return null;
  }
  const num = parseInt(val, 10);
  return Number.isNaN(num) ? null : num;
};

interface ConfidenceRangeValidationOptions {
  externalMinError?: string | null | Ref<string | null> | ComputedRef<string | null>;
  externalMaxError?: string | null | Ref<string | null> | ComputedRef<string | null>;
}

export const useConfidenceRangeValidation = (
  min: ComputedRef<number | null>,
  max: ComputedRef<number | null>,
  options: Partial<ConfidenceRangeValidationOptions> = {},
) => {
  const minError = computed((): string | null => {
    const minValue = min.value;
    const maxValue = max.value;

    if (minValue !== null && (minValue < 0 || minValue > 100)) {
      return t('ai-photo-validation.confidence-out-of-range');
    }

    if (minValue !== null && maxValue !== null && minValue > maxValue) {
      return t('ai-photo-validation.confidence-min-greater-than-max');
    }

    if (options.externalMinError) {
      const error = unref(options.externalMinError);
      return error as string | null;
    }

    return null;
  });

  const maxError = computed((): string | null => {
    const minValue = min.value;
    const maxValue = max.value;

    if (maxValue !== null && (maxValue < 0 || maxValue > 100)) {
      return t('ai-photo-validation.confidence-out-of-range');
    }

    if (minValue !== null && maxValue !== null && maxValue < minValue) {
      return t('ai-photo-validation.confidence-max-less-than-min');
    }

    if (options.externalMaxError) {
      const error = unref(options.externalMaxError);
      return error as string | null;
    }

    return null;
  });

  const hasValidationErrors = computed(() => !!minError.value || !!maxError.value);

  return {
    minError,
    maxError,
    hasValidationErrors,
  };
};
